// apps/client/src/game/interactions/museum.ts (M2-T10) — the museum door outdoors (sites.museumDoor), and inside: Professor Reed at
// his lectern (identify, donate, the collection), each fossil display (its plaque) and the way out. Ranges match the server's checks:
// doors tuning.town.doorRange from the door tile centre, the curator tuning.town.interactRange from tuning.spaces.museum.curator.
import { tuning } from '@whiskerton/content';
import type { TileXY, Vec2 } from '@whiskerton/shared';
import { useGame } from '../../state/store';
import { curatorStandPoint, museumSlots } from '../../world/spaces/museum';
import type { Candidate, InteractCtx, Interaction } from '../interact';

const L = () => tuning.spaces.museum;
const d = (a: Vec2, b: Vec2) => Math.hypot(a.x - b.x, a.y - b.y);
const centre = (t: TileXY): Vec2 => ({ x: t.x + 0.5, y: t.y + 0.5 });

const enter = (at: Vec2): Candidate => ({ id: 'museumDoor', at, verb: 'hud.verbEnter', priority: 10 });
const exit = (at: Vec2): Candidate => ({ id: 'exit', at, verb: 'hud.verbExit', priority: 10 });
const curator = (at: Vec2): Candidate => ({ id: 'curator', at, verb: 'hud.verbTalk', priority: 70 });
const display = (set: string, at: Vec2): Candidate => ({ id: `display:${set}`, at, verb: 'hud.verbRead', priority: 40 });
/** Where a display's plaque reads from: the middle of a plinth's front, or the wall plaque itself. */
const displayAt = (s: ReturnType<typeof museumSlots>[number]): Vec2 => (s.mount === 'floor' ? { x: s.x + s.w / 2, y: s.y + 0.4 } : { x: s.x, y: s.y });

export const interaction: Interaction = {
  id: 'museum',
  find(ctx) {
    if (ctx.space === 'out') {
      const site = ctx.store.map?.sites.museumDoor;                        // WorldSlice.map (revision 4)
      return site && d(ctx.pos, centre(site)) <= tuning.town.doorRange ? enter(centre(site)) : null;
    }
    if (ctx.space !== 'museum') return null;
    const [cx, cy] = L().curator;
    const cands: Candidate[] = [];
    if (d(ctx.pos, { x: cx, y: cy }) <= tuning.town.interactRange) cands.push(curator({ x: cx, y: cy }));
    for (const s of museumSlots()) {
      const at = displayAt(s);
      if (d(ctx.pos, at) <= tuning.town.interactRange) cands.push(display(s.set, at));
    }
    const [dx, dy] = L().door;
    if (d(ctx.pos, { x: dx + 0.5, y: dy + 0.5 }) <= tuning.town.doorRange) cands.push(exit({ x: dx + 0.5, y: dy + 0.5 }));
    return cands.sort((a, b) => b.priority - a.priority || d(ctx.pos, a.at) - d(ctx.pos, b.at))[0] ?? null;
  },
  run(ctx, c) { runCandidate(ctx, c); },
  /** A tap on the museum or its door, the curator or his lectern, or the way out: walk there, then run it. `at` is where to stand. */
  fromTap(ctx, hit) {
    const t = hit.tile;
    if (ctx.space === 'out') {
      const site = ctx.store.map?.sites.museumDoor;
      if (!site) return null;
      return (site.x === t.x && site.y === t.y) || hit.object?.catalogId === 'w.museum' ? enter(centre(site)) : null;
    }
    if (ctx.space !== 'museum') return null;
    const [cx, cy] = L().curator, [dx, dy] = L().door;
    // the free tile centre in front of the lectern, within interactRange (walks floor to a tile and stop at its centre)
    if (Math.abs(t.x + 0.5 - cx) <= 0.6 && t.y >= Math.floor(cy) - 1 && t.y <= Math.floor(cy)) return curator(curatorStandPoint());
    if (t.x === dx && t.y === dy) return exit(centre(t));
    return null;
  },
};

function runCandidate(ctx: InteractCtx, c: Candidate): void {
  const open = useGame.getState().openScreen;
  if (c.id === 'museumDoor') void ctx.game.cmd.enterSpace('museum');
  else if (c.id === 'exit') void ctx.game.cmd.enterSpace('out');
  else if (c.id === 'curator') open('museum', { mode: 'curator' });
  else if (c.id.startsWith('display:')) open('museum', { mode: 'plaque', set: c.id.slice('display:'.length) });
}
